import { Icon, IconButton } from '@smartgift/frontend-toolkit';
import { isArray } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Table } from '@hero/molecules';
import { isEmailValid } from '@hero/utils/validationUtils';

const Wrapper = styled.div``;

const SpreadSheetEmailTable = ({ emails, updateEmails }) => {
  const { t } = useTranslation();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});

  const pageCount = useMemo(() => {
    return Math.ceil(emails.length / pageSize);
  }, []);

  const emailsByPage = useMemo(() => {
    let pageIndex = 0;
    const result = {};

    while (pageIndex < pageCount) {
      result[pageIndex] = emails.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
      pageIndex += 1;
    }

    return result;
  }, [emails]);

  const columns = React.useMemo(
    () => [
      {
        id: 'email',
        header: t('email'),
        editable: true,
        size: 500,
        accessorKey: 'email',
        isValidFn: val => isEmailValid(val),
        componentProps: {
          maxLength: 100,
          isValidInput: val => isEmailValid(val)
        }
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ table, row }) => {
          const { updateEmails } = table.getState();

          return (
            <IconButton
              variant="text"
              color="error"
              onClick={() => {
                updateEmails(emails => {
                  return emails.filter(email => email.id !== row.id);
                });
              }}
            >
              <Icon name="delete" size="lg" />
            </IconButton>
          );
        }
      }
    ],
    []
  );

  const updateData = (originalRow, columnId, value) => {
    const currentValue = originalRow[columnId];

    const isDifferent = isArray(value)
      ? !isEqual(
          currentValue?.map?.(col => col.id),
          value?.map?.(val => val.value)
        )
      : !isEqual(currentValue, value);

    if (!isDifferent) return;

    updateEmails(emails =>
      emails?.map(email => {
        if (email.id !== originalRow.id) return email;

        const updatedEmail = {
          ...email,
          [columnId]: value,
          hasError: !isEmailValid(email.email)
        };

        return updatedEmail;
      })
    );
  };

  return (
    <Wrapper>
      <Table
        columns={columns}
        data={emailsByPage[pageIndex] || []}
        state={{
          updateEmails,
          globalFilter: globalFilters
        }}
        updateData={updateData}
        setPagination={setPagination}
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        useGlobalFuzzyFilter
        manualPagination
        pagination={{
          pageIndex,
          pageSize,
          total: emails?.length
        }}
        rowHeight={52}
        pageCount={pageCount}
        enabled={{ filter: false, header: true }}
      />
    </Wrapper>
  );
};

export { SpreadSheetEmailTable };
